/**
 * PlotReadinessPill — Plot element readiness analyzer.
 *
 * Analyses the manuscript against 6 structural narrative elements,
 * detects plot transition gaps, malformed/truncated chapters, and
 * character inconsistencies. Provides per-issue fix generators and
 * a "Fix All" option.
 *
 * Uses async background job pattern (same as ArcWalkerDrawer):
 *   POST /api/projects/{pid}/plot-readiness  → {job_id, status: "running"}
 *   GET  /api/projects/{pid}/plot-readiness/status  → polls until done
 *
 * Fix jobs:
 *   POST /api/projects/{pid}/plot-readiness/fix  → {job_id}
 *   GET  /api/projects/{pid}/plot-readiness/fix/{job_id}  → polls
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  AlertTriangle, BookMarked, BookOpen, CheckCircle2, CheckCheck,
  ChevronDown, ChevronUp, Copy, FileWarning, GitBranch,
  Loader2, RefreshCw, User, Wand2, X, XCircle, Wrench,
  Type, Bold, ZoomIn, ZoomOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;
const LS_READING = 'loom:reading_prefs_plot';

/* ─── Reading prefs (font size + bold) ──────────────────────────────────── */

const FONT_SIZES = [
  { key: 'sm',  label: 'S',  body: '11.5px', heading: '13px' },
  { key: 'md',  label: 'M',  body: '12.5px', heading: '14px' },
  { key: 'lg',  label: 'L',  body: '14px',   heading: '15.5px' },
  { key: 'xl',  label: 'XL', body: '16px',   heading: '17px' },
];

function readingPrefsFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(LS_READING) || '{}');
  } catch { return {}; }
}

function ReadingControls({ prefs, setPrefs }) {
  const currentIdx = FONT_SIZES.findIndex((f) => f.key === prefs.fontSize) ?? 1;
  const safeIdx = currentIdx < 0 ? 1 : currentIdx;

  const cycleFont = (dir) => {
    const next = Math.max(0, Math.min(FONT_SIZES.length - 1, safeIdx + dir));
    const updated = { ...prefs, fontSize: FONT_SIZES[next].key };
    setPrefs(updated);
    localStorage.setItem(LS_READING, JSON.stringify(updated));
  };

  const toggleBold = () => {
    const updated = { ...prefs, bold: !prefs.bold };
    setPrefs(updated);
    localStorage.setItem(LS_READING, JSON.stringify(updated));
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => cycleFont(-1)}
        disabled={safeIdx === 0}
        className="flex h-6 w-6 items-center justify-center rounded-sm border border-border text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-30 transition"
        title="Smaller text"
      >
        <ZoomOut className="h-3 w-3" />
      </button>
      <span className="w-5 text-center text-[13px] font-mono text-muted-foreground">
        {FONT_SIZES[safeIdx].label}
      </span>
      <button
        type="button"
        onClick={() => cycleFont(1)}
        disabled={safeIdx === FONT_SIZES.length - 1}
        className="flex h-6 w-6 items-center justify-center rounded-sm border border-border text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-30 transition"
        title="Larger text"
      >
        <ZoomIn className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={toggleBold}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-sm border text-[13px] transition',
          prefs.bold
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
        title={prefs.bold ? 'Disable bold body text' : 'Enable bold body text'}
      >
        <Bold className="h-3 w-3" />
      </button>
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function pillColour(score) {
  if (score >= 83) return 'bg-emerald-600 text-white border-emerald-700';
  if (score >= 50) return 'bg-amber-500 text-white border-amber-600';
  return 'bg-rose-600 text-white border-rose-700';
}

function statusBadge(status) {
  if (status === 'present')
    return <Badge className="gap-1 bg-emerald-50 text-emerald-800 border-emerald-300 font-medium text-[13px]"><CheckCircle2 className="h-3 w-3" /> Present</Badge>;
  if (status === 'weak')
    return <Badge className="gap-1 bg-amber-50 text-amber-800 border-amber-300 font-medium text-[13px]"><AlertTriangle className="h-3 w-3" /> Weak</Badge>;
  return <Badge className="gap-1 bg-rose-50 text-rose-800 border-rose-300 font-medium text-[13px]"><XCircle className="h-3 w-3" /> Missing</Badge>;
}

function SeverityBadge({ severity }) {
  const MAP = {
    high:   'bg-rose-50 text-rose-800 border-rose-300',
    medium: 'bg-amber-50 text-amber-800 border-amber-300',
    low:    'bg-sky-50 text-sky-800 border-sky-300',
  };
  return (
    <span className={cn('inline-flex rounded-sm border px-1.5 py-0.5 text-[13px] font-semibold uppercase tracking-wide', MAP[severity] || MAP.medium)}>
      {severity}
    </span>
  );
}

const ELEMENT_ORDER = ['exposition', 'inciting_incident', 'rising_action', 'climax', 'falling_action', 'resolution'];

/* ─── FixResultPanel ─────────────────────────────────────────────────────── */

function FixResultPanel({ result, bodyStyle, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  // "Fix All" has a list of suggestions — render them flat
  if (result.fix_type === 'all' && Array.isArray(result.suggestions)) {
    return (
      <div className="space-y-3">
        {result.suggestions.map((s, i) => (
          <SingleFixResult key={i} result={s} bodyStyle={bodyStyle} />
        ))}
        {onClose && (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClose} className="h-6 text-[13px]">Close</Button>
          </div>
        )}
      </div>
    );
  }

  return <SingleFixResult result={result} bodyStyle={bodyStyle} onClose={onClose} />;
}

function SingleFixResult({ result, bodyStyle, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  const prose = result.suggested_prose || result.continuation_prose || result.suggested_rewrite || '';
  const placement = result.placement_note || result.closing_note || result.explanation || '';
  const insertAfter = result.insert_after_chapter ?? result.chapter_number;
  const insertPos = result.insert_position;

  const copy = () => {
    navigator.clipboard.writeText(prose).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-2.5 space-y-2 rounded-md border border-primary/20 bg-primary/5 p-3">
      {result.issue_label && (
        <p className="text-[13px] font-semibold text-primary uppercase tracking-wide">{result.issue_label}</p>
      )}
      {placement && (
        <div className="rounded-sm border border-sky-200/70 bg-sky-50 px-2.5 py-1.5">
          <p className="text-[13px] font-semibold text-sky-800 uppercase tracking-wide mb-0.5">
            {insertAfter ? `Insert after Ch. ${insertAfter}` : insertPos ? `Insert at ${insertPos.replace(/_/g,' ')}` : 'Placement'}
          </p>
          <p style={bodyStyle} className="text-sky-900">{placement}</p>
        </div>
      )}
      {prose && (
        <div className="relative">
          <ScrollArea className="h-48 rounded-sm border border-border bg-muted/20 p-2.5">
            <pre className="whitespace-pre-wrap font-serif leading-relaxed text-foreground" style={bodyStyle}>
              {prose}
            </pre>
          </ScrollArea>
          <button
            type="button"
            onClick={copy}
            className="absolute top-2 right-2 flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-0.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Copy className="h-2.5 w-2.5" />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}
      {onClose && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 text-[13px]">Close</Button>
        </div>
      )}
    </div>
  );
}

/* ─── FixButton — per-item async fix trigger ─────────────────────────────── */

function FixButton({ label, icon: Icon = Wand2, fixPayload, projectId, token, bodyStyle }) {
  const [state, setState] = useState({ status: 'idle', jobId: null, result: null, error: null });
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPoll(), []);

  const startPoll = useCallback((jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/plot-readiness/fix/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (data.status === 'done') {
          stopPoll();
          setState({ status: 'done', jobId, result: data.result, error: null });
        } else if (data.status === 'failed') {
          stopPoll();
          setState({ status: 'failed', jobId, result: null, error: data.error });
          toast.error(data.error || 'Fix generation failed.');
        }
      } catch { /* keep polling on transient errors */ }
    }, POLL_MS);
  }, [projectId, token]);

  const runFix = async () => {
    if (state.status === 'loading') return;
    setState({ status: 'loading', jobId: null, result: null, error: null });
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/plot-readiness/fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(fixPayload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Fix request failed');
      }
      const { job_id } = await r.json();
      setState((s) => ({ ...s, jobId: job_id }));
      startPoll(job_id);
    } catch (e) {
      setState({ status: 'failed', jobId: null, result: null, error: e.message });
      toast.error(e.message);
    }
  };

  const reset = () => setState({ status: 'idle', jobId: null, result: null, error: null });

  if (state.status === 'done' && state.result) {
    return (
      <div>
        <FixResultPanel result={state.result} bodyStyle={bodyStyle} onClose={reset} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1.5 text-[13px] border-primary/40 text-primary hover:bg-primary/10"
        onClick={state.status === 'done' ? reset : runFix}
        disabled={state.status === 'loading'}
        data-testid={`fix-btn-${fixPayload.fix_type}`}
      >
        {state.status === 'loading'
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <Icon className="h-3 w-3" />}
        {state.status === 'loading' ? 'Generating…' : label}
      </Button>
      {state.status === 'failed' && (
        <span className="text-[13px] text-rose-600">{state.error}</span>
      )}
    </div>
  );
}

/* ─── ElementRow ─────────────────────────────────────────────────────────── */

function ElementRow({ element, projectId, token, bodyStyle, headingStyle }) {
  const [expanded, setExpanded] = useState(false);
  const needsAttention = element.status === 'missing' || element.status === 'weak';

  return (
    <div className={cn('rounded-lg border transition-colors', element.status === 'present' ? 'border-border' : 'border-amber-200/80')}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
        data-testid={`plot-element-row-${element.key}`}
      >
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted/50 text-[13px] font-bold text-muted-foreground">
          {ELEMENT_ORDER.indexOf(element.key) + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={headingStyle} className="font-semibold text-foreground">{element.label}</span>
            {statusBadge(element.status)}
          </div>
          {element.chapter_hint && (
            <span className="text-[13px] text-muted-foreground">Ch. {element.chapter_hint}</span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="border-t border-border/50 px-3 py-2.5 space-y-2.5">
          <p style={bodyStyle} className="text-muted-foreground leading-relaxed">{element.description}</p>
          {element.note && (
            <p style={bodyStyle} className="text-foreground italic leading-relaxed">"{element.note}"</p>
          )}
          {needsAttention && (
            <FixButton
              label={`Fix ${element.label}`}
              icon={Wand2}
              fixPayload={{ fix_type: 'element', target_key: element.key }}
              projectId={projectId}
              token={token}
              bodyStyle={bodyStyle}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ─── MalformedChapterCard ───────────────────────────────────────────────── */

function MalformedChapterCard({ item, projectId, token, bodyStyle }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-md border border-rose-200/80 bg-rose-50/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-rose-50/60 transition-colors"
        data-testid={`malformed-ch-${item.chapter_number}`}
      >
        <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '12px', fontWeight: 600 }} className="text-foreground">Ch. {item.chapter_number} — {item.title}</span>
            <SeverityBadge severity={item.severity} />
          </div>
          <p style={bodyStyle} className="text-muted-foreground mt-0.5">Ends without proper sentence termination</p>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="border-t border-rose-200/50 px-3 py-2 space-y-2">
          <p style={bodyStyle} className="text-muted-foreground font-mono break-all">{item.detail}</p>
          <FixButton
            label="Complete this chapter"
            icon={Wrench}
            fixPayload={{ fix_type: 'malformed', chapter_number: item.chapter_number }}
            projectId={projectId}
            token={token}
            bodyStyle={bodyStyle}
          />
        </div>
      )}
    </div>
  );
}

/* ─── TransitionCard ─────────────────────────────────────────────────────── */

function TransitionCard({ item, projectId, token, bodyStyle }) {
  const [expanded, setExpanded] = useState(false);
  const isAbrupt = item.quality === 'abrupt';
  const borderCls = isAbrupt ? 'border-rose-200/80 bg-rose-50/30' : 'border-amber-200/60 bg-amber-50/30';
  return (
    <div className={cn('rounded-md border', borderCls)}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-muted/20 transition-colors"
        data-testid={`transition-ch-${item.from_chapter}-${item.to_chapter}`}
      >
        <GitBranch className={cn('mt-0.5 h-4 w-4 shrink-0', isAbrupt ? 'text-rose-600' : 'text-amber-600')} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: '12px', fontWeight: 600 }} className="text-foreground">
              Ch. {item.from_chapter} → Ch. {item.to_chapter}
            </span>
            <span className={cn('inline-flex rounded-sm px-1.5 py-0.5 text-[13px] font-semibold uppercase tracking-wide', isAbrupt ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800')}>
              {item.quality}
            </span>
          </div>
          <p style={bodyStyle} className="text-muted-foreground mt-0.5 line-clamp-1">{item.note}</p>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="border-t border-border/40 px-3 py-2 space-y-2">
          <p style={bodyStyle} className="text-muted-foreground">{item.note}</p>
          <FixButton
            label="Fix this transition"
            icon={Wrench}
            fixPayload={{ fix_type: 'transition', from_chapter: item.from_chapter, to_chapter: item.to_chapter }}
            projectId={projectId}
            token={token}
            bodyStyle={bodyStyle}
          />
        </div>
      )}
    </div>
  );
}

/* ─── CharacterIssueCard ─────────────────────────────────────────────────── */

function CharacterIssueCard({ item, projectId, token, bodyStyle }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-md border border-amber-200/70 bg-amber-50/30">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-amber-50/50 transition-colors"
        data-testid={`char-issue-${item.chapter_number}`}
      >
        <User className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '12px', fontWeight: 600 }} className="text-foreground">{item.character}</span>
            {item.chapter_number && <span className="text-[13px] text-muted-foreground">Ch. {item.chapter_number}</span>}
            <SeverityBadge severity={item.severity} />
          </div>
          <p style={bodyStyle} className="text-muted-foreground mt-0.5 line-clamp-1">{item.issue}</p>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="border-t border-amber-200/50 px-3 py-2 space-y-2">
          <p style={bodyStyle} className="text-muted-foreground">{item.issue}</p>
          <FixButton
            label="Fix this inconsistency"
            icon={Wand2}
            fixPayload={{ fix_type: 'character', character: item.character, chapter_number: item.chapter_number }}
            projectId={projectId}
            token={token}
            bodyStyle={bodyStyle}
          />
        </div>
      )}
    </div>
  );
}

/* ─── SectionBlock — collapsible section wrapper ─────────────────────────── */

function SectionBlock({ icon: Icon, title, count, defaultOpen = true, children, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
      >
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-primary" />}
        <span className="flex-1 text-[13.5px] font-semibold">{title}</span>
        {badge}
        {count !== undefined && (
          <span className="mr-1 inline-flex items-center justify-center rounded-full bg-muted px-1.5 text-[13px] text-muted-foreground">
            {count}
          </span>
        )}
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && <div className="border-t border-border/50 px-3 pb-3 pt-2 space-y-2">{children}</div>}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function PlotReadinessPill({ projectId, compact = false, stale = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [cached, setCached] = useState(null);
  const [result, setResult] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [prefs, setPrefs] = useState(() => ({ fontSize: 'md', bold: false, ...readingPrefsFromStorage() }));
  const pollRef = useRef(null);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const displayed = result || cached;
  const isStale = result ? false : (stale || !!cached?.stale);

  const currentFont = FONT_SIZES.find((f) => f.key === prefs.fontSize) || FONT_SIZES[1];
  const bodyStyle = { fontSize: currentFont.body, fontWeight: prefs.bold ? 600 : 400 };
  const headingStyle = { fontSize: currentFont.heading };

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/plot-readiness/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (data.status === 'done' && data.result) {
          stopPolling();
          setResult(data.result);
          setCached(data.result);
          setLoading(false);
          toast.success('Plot readiness analysis complete');
        } else if (data.status === 'failed') {
          stopPolling();
          setLoading(false);
          setJobError(data.error || 'Analysis failed.');
          toast.error(data.error || 'Plot readiness analysis failed. Please retry.');
        }
        // status === 'running' → keep polling
      } catch { /* transient error — keep polling */ }
    }, POLL_MS);
  }, [projectId, token, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Load cached result on mount
  useEffect(() => {
    if (!projectId) return;
    fetch(`${API}/api/projects/${projectId}/plot-readiness`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCached(d))
      .catch(() => {});
  }, [projectId, token]);

  const runAnalysis = async () => {
    if (!projectId || loading) return;
    setLoading(true);
    setJobError(null);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/plot-readiness`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start analysis');
        setLoading(false);
        return;
      }
      const data = await r.json();
      setJobId(data.job_id);
      startPolling();
    } catch (e) {
      setLoading(false);
      toast.error(`Failed to start analysis: ${e.message}`);
    }
  };

  const score = displayed?.score;
  const elements = (displayed?.elements || []).sort(
    (a, b) => ELEMENT_ORDER.indexOf(a.key) - ELEMENT_ORDER.indexOf(b.key),
  );
  const malformed = displayed?.malformed_chapters || [];
  const transitions = displayed?.plot_transitions || [];
  const charIssues = displayed?.character_inconsistencies || [];
  const missingCount = elements.filter((e) => e.status === 'missing').length;
  const presentCount = elements.filter((e) => e.status === 'present').length;
  const totalIssues = malformed.length + transitions.length + charIssues.length;

  // Collect all issues for "Fix All"
  const hasFixableIssues = missingCount > 0 || elements.some((e) => e.status === 'weak') || totalIssues > 0;

  return (
    <>
      {/* Pill button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="plot-readiness-pill"
        title={isStale && score != null ? 'Plot readiness — re-run needed (new chapters added)' : 'Plot element readiness'}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] font-semibold shadow-sm transition-all hover:scale-105 active:scale-95',
          score == null
            ? 'border-border bg-muted/50 text-muted-foreground hover:bg-muted'
            : pillColour(score),
        )}
      >
        <BookMarked className="h-3 w-3 shrink-0" />
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : (score == null ? 'Plot' : `${score}%`)}
        {isStale && score != null && (
          <span className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/30 text-[9px] font-bold" title="Re-run needed">!</span>
        )}
        {!isStale && score != null && missingCount > 0 && (
          <span className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/30 text-[9px] font-bold">{missingCount}</span>
        )}
      </button>

      {/* Main analysis dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) stopPolling(); setOpen(v); }}>
        <DialogContent
          className="flex flex-col p-0 overflow-hidden"
          style={{ maxWidth: '67vw', width: '67vw', maxHeight: '85vh' }}
          data-testid="plot-readiness-dialog"
        >
          {/* Header */}
          <DialogHeader className="shrink-0 border-b border-border bg-panel-header/40 px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="flex items-center gap-2 text-[14px]">
                <BookOpen className="h-4 w-4 text-primary" />
                Plot Element Readiness
                {totalIssues > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-rose-500 px-1.5 text-[13px] font-semibold text-white">
                    {totalIssues} issues
                  </span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <ReadingControls prefs={prefs} setPrefs={setPrefs} />
                <Separator orientation="vertical" className="h-5" />
                {hasFixableIssues && displayed && (
                  <FixButton
                    label="Fix All Issues"
                    icon={Wand2}
                    fixPayload={{ fix_type: 'all' }}
                    projectId={projectId}
                    token={token}
                    bodyStyle={bodyStyle}
                  />
                )}
                <Button
                  size="sm"
                  variant={score == null ? 'default' : 'outline'}
                  onClick={runAnalysis}
                  disabled={loading}
                  className="h-7 gap-1.5 text-[13px]"
                  data-testid="plot-readiness-run-btn"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  {loading ? 'Analyzing…' : score == null ? 'Analyse' : 'Re-run'}
                </Button>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 min-h-0">
            <div className="px-5 py-4 space-y-4">
              {/* Loading state */}
              {loading && !displayed && (
                <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-center">
                    Reading all chapters — analyzing in the background…
                    <br />
                    <span className="text-[13px] opacity-70">Results appear automatically when ready.</span>
                  </p>
                </div>
              )}

              {/* Error state */}
              {!loading && jobError && (
                <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-[13.5px] text-rose-700">
                  <p className="font-medium">Analysis failed</p>
                  <p className="mt-0.5 opacity-80">{jobError}</p>
                  <Button size="sm" variant="outline" className="mt-2 h-6 text-[13px]" onClick={runAnalysis}>
                    Try again
                  </Button>
                </div>
              )}

              {/* Stale warning */}
              {isStale && score != null && !loading && (
                <div className="flex items-center gap-2 rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2" data-testid="plot-readiness-stale-banner">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <p style={bodyStyle} className="text-amber-900">
                    <strong>Stale:</strong> chapters have been drafted since this analysis was run. Re-run for an accurate score.
                  </p>
                </div>
              )}

              {/* Score summary */}
              <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3">
                {score == null ? (
                  <div className="flex-1" style={bodyStyle}>
                    <p className="text-muted-foreground">Run the analysis to see your plot structure score.</p>
                  </div>
                ) : (
                  <>
                    <div className={cn('flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 text-[20px] font-black', pillColour(score))}>
                      {score}
                    </div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div style={headingStyle} className="font-semibold">
                        {score >= 83 ? 'Structurally solid' : score >= 50 ? 'Needs attention' : 'Major gaps found'}
                      </div>
                      <div style={bodyStyle} className="text-muted-foreground">
                        {presentCount} of 6 elements present{missingCount > 0 ? ` · ${missingCount} missing` : ''}
                        {displayed?.chapters_analysed ? ` · ${displayed.chapters_analysed} chapters analysed` : ''}
                        {totalIssues > 0 ? ` · ${totalIssues} issue${totalIssues !== 1 ? 's' : ''} detected` : ''}
                      </div>
                      {displayed?.overall_note && (
                        <p style={bodyStyle} className="italic text-muted-foreground leading-snug">
                          {displayed.overall_note}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Progress bar */}
              {score != null && (
                <div className="space-y-1 px-1">
                  <div className="flex justify-between text-[13px] text-muted-foreground mb-1">
                    <span>Structural completeness</span>
                    <span>{score}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all duration-700', score >= 83 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-rose-500')}
                      style={{ width: `${score}%` }}
                    />
                  </div>
                </div>
              )}

              {/* ── NEW: Malformed Chapters ──────────────────────────────── */}
              {malformed.length > 0 && (
                <SectionBlock
                  icon={FileWarning}
                  title="Malformed Chapters"
                  count={malformed.length}
                  defaultOpen={true}
                  badge={<span className="mr-1 inline-flex items-center justify-center rounded-full bg-rose-500 px-1.5 text-[13px] font-semibold text-white">{malformed.length}</span>}
                >
                  {malformed.map((m, i) => (
                    <MalformedChapterCard key={i} item={m} projectId={projectId} token={token} bodyStyle={bodyStyle} />
                  ))}
                </SectionBlock>
              )}

              {/* ── NEW: Plot Transitions ────────────────────────────────── */}
              {transitions.length > 0 && (
                <SectionBlock
                  icon={GitBranch}
                  title="Plot Transition Issues"
                  count={transitions.length}
                  defaultOpen={transitions.some((t) => t.quality === 'abrupt')}
                >
                  {transitions.map((t, i) => (
                    <TransitionCard key={i} item={t} projectId={projectId} token={token} bodyStyle={bodyStyle} />
                  ))}
                </SectionBlock>
              )}

              {/* ── NEW: Character Inconsistencies ───────────────────────── */}
              {charIssues.length > 0 && (
                <SectionBlock
                  icon={User}
                  title="Character Inconsistencies"
                  count={charIssues.length}
                  defaultOpen={charIssues.some((c) => c.severity === 'high')}
                >
                  {charIssues.map((ci, i) => (
                    <CharacterIssueCard key={i} item={ci} projectId={projectId} token={token} bodyStyle={bodyStyle} />
                  ))}
                </SectionBlock>
              )}

              {/* ── Narrative Elements ───────────────────────────────────── */}
              <Separator />
              <SectionBlock icon={BookOpen} title="Narrative Elements" count={elements.length} defaultOpen={true}>
                {elements.length === 0 ? (
                  <p style={bodyStyle} className="text-center text-muted-foreground py-4">Run the analysis to see element breakdown</p>
                ) : (
                  elements.map((el) => (
                    <ElementRow
                      key={el.key}
                      element={el}
                      projectId={projectId}
                      token={token}
                      bodyStyle={bodyStyle}
                      headingStyle={headingStyle}
                    />
                  ))
                )}
              </SectionBlock>

              {/* Footer */}
              {displayed?.analysed_at && (
                <p className="text-[13px] text-muted-foreground text-right pb-1">
                  Last analysed {new Date(displayed.analysed_at).toLocaleDateString()}
                  {displayed.route ? ` · via ${displayed.route}` : ''}
                </p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}


/* ─── PlotCompletenessBanner — exported for ChaptersPanel ─────────────────
 *
 * Shows a visible warning banner when the plot analysis finds missing or
 * weak narrative elements.  Includes a "Fix All Missing" quick action that
 * calls the fix endpoint directly without opening the full dialog.
 *
 * Props:
 *   projectId        — required, the project id
 *   plotReadiness    — project.plot_readiness (may be null/undefined)
 *   stale            — project.plot_readiness_stale (boolean)
 */
export function PlotCompletenessBanner({ projectId, plotReadiness, stale }) {
  const [dismissed, setDismissed] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixDone, setFixDone] = useState(false);
  const [fixError, setFixError] = useState(null);
  const pollRef = useRef(null);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // Stop polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (!plotReadiness || dismissed) return null;

  const elements = plotReadiness.elements || [];
  const missing = elements.filter((e) => e.status === 'missing').map((e) => e.label);
  const weak = elements.filter((e) => e.status === 'weak').map((e) => e.label);

  // Only show when there are actionable issues
  if (missing.length === 0 && weak.length === 0) return null;

  const isCritical = missing.length > 0;
  const labelList = isCritical ? missing : weak;

  const runFix = async () => {
    if (fixing) return;
    setFixing(true);
    setFixError(null);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/plot-readiness/fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fix_type: 'all' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Fix request failed');
      }
      const { job_id } = await r.json();
      pollRef.current = setInterval(async () => {
        try {
          const p = await fetch(
            `${API}/api/projects/${projectId}/plot-readiness/fix/${job_id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const d = await p.json();
          if (d.status === 'done' || d.status === 'failed') {
            clearInterval(pollRef.current);
            setFixing(false);
            if (d.status === 'done') {
              setFixDone(true);
              toast.success('Fix suggestions generated — open Plot Readiness to review.');
            } else {
              setFixError(d.error || 'Fix generation failed.');
              toast.error(d.error || 'Fix generation failed.');
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      setFixing(false);
      setFixError(e.message);
      toast.error(e.message);
    }
  };

  if (fixDone) {
    return (
      <div
        className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13.5px] text-emerald-800"
        data-testid="plot-completeness-banner"
      >
        <CheckCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <span className="flex-1">Fix suggestions generated. Open the Plot Readiness panel to review and apply them.</span>
        <button
          onClick={() => setDismissed(true)}
          className="rounded p-0.5 hover:bg-emerald-100 transition-colors opacity-60 hover:opacity-100"
          title="Dismiss"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mx-3 mt-2 flex items-start gap-3 rounded-md border px-3 py-2.5 text-[13.5px]',
        isCritical
          ? 'border-rose-200 bg-rose-50 text-rose-900'
          : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
      data-testid="plot-completeness-banner"
    >
      <AlertTriangle
        className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', isCritical ? 'text-rose-500' : 'text-amber-500')}
      />
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="font-semibold">
          {isCritical ? 'Missing narrative elements' : 'Narrative elements need attention'}:&nbsp;
          <span className="font-normal">{labelList.join(', ')}</span>
        </p>
        {stale && (
          <p className="text-[13px] opacity-70">
            Analysis may be outdated — re-run the plot check for an accurate result.
          </p>
        )}
        {fixError && <p className="text-[13px] text-rose-600 mt-0.5">{fixError}</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
        <button
          onClick={runFix}
          disabled={fixing}
          data-testid="plot-completeness-fix-btn"
          className={cn(
            'flex items-center gap-1 rounded border px-2 py-0.5 text-[13px] font-medium transition-colors disabled:opacity-50',
            isCritical
              ? 'border-rose-300 text-rose-700 hover:bg-rose-100'
              : 'border-amber-300 text-amber-700 hover:bg-amber-100',
          )}
        >
          {fixing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          {fixing ? 'Generating…' : 'Fix All Missing'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="rounded p-0.5 hover:bg-black/10 transition-colors opacity-50 hover:opacity-100"
          title="Dismiss"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
